import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * 通知佇列（畫面層）：右上角固定一疊，一則一張卡，彼此**垂直堆疊不覆蓋**。
 *
 * 舊的 `ErrorBar` 是「內容區最上方一條、`string | null`」——第二筆一定蓋掉第一筆，
 * 這就是 #03（一次註冊多顆，只看得到最後一顆的結果）的根因。這裡改成：
 * - 位置：`position: fixed` 右上（避開頂列與三欄），寬 360、`max-height: 60vh` 可滾
 * - 一則一張：`data-testid="notice-item"`，各自帶類型標籤（錯誤／警告／成功／提示）、時間、×
 * - 錯誤（error）黏在畫面上直到按 ×；其餘由 `useNoticeStack` 的 TTL 收掉
 * 不改版面流（position: fixed），所以不會推擠清單或對話串。
 *
 * ## 手機（<768px，styles.css 的 `.cui-notice-stack` 規則）
 *
 * 原本 390 寬時這一疊從 `top:44` 往下長到 `60vh`，**蓋住底部四格導覽並攔掉點擊**
 * （z-index 60 > 導覽的 40），而 sticky 錯誤永遠不會自己消失 → 使用者卡死在分區。
 * 改法＝「導覽上方召喚式細條」：
 * - 幾何：`bottom: calc(3.5rem + env(safe-area-inset-bottom) + 8px)`，也就是**永遠長在
 *   導覽（Tailwind `h-14`＝3.5rem，本專案根字級 14px → 實測 49px）之上**，`top:auto`，
 *   高度只往上長——它不可能碰到導覽那一段。
 * - 預設收合成一條（最多 2 行）：類型＋則數＋最新一則訊息，點一下展成完整列表，再點收合。
 * - DOM 兩種尺寸完全一樣（收合只是 CSS `display:none`），所以桌面與測試看到的結構不變。
 */
import { useState } from 'react';
import { NOTICE_KIND_LABEL, noticeClock } from '../notices';
/** 顏色跟 console 既有的語意走：錯誤＝紅、警告＝琥珀、成功＝綠、提示＝中性。 */
const KIND_STYLE = {
    error: {
        border: 'rgba(220, 38, 38, 0.45)',
        background: 'color-mix(in srgb, #dc2626 12%, var(--ak-color-surface, #fff))',
        foreground: 'var(--ak-color-text, #111)',
        badge: '#dc2626',
    },
    warning: {
        border: 'rgba(217, 119, 6, 0.45)',
        background: 'color-mix(in srgb, #d97706 12%, var(--ak-color-surface, #fff))',
        foreground: 'var(--ak-color-text, #111)',
        badge: '#b45309',
    },
    success: {
        border: 'rgba(22, 163, 74, 0.45)',
        background: 'color-mix(in srgb, #16a34a 10%, var(--ak-color-surface, #fff))',
        foreground: 'var(--ak-color-text, #111)',
        badge: '#15803d',
    },
    info: {
        border: 'var(--ak-color-border, rgba(0,0,0,0.14))',
        background: 'var(--ak-color-surface, #fff)',
        foreground: 'var(--ak-color-text, #111)',
        badge: 'var(--ak-color-text-tertiary, #6b7280)',
    },
};
export function NoticeStack({ notices, onDismiss, onRetry, top = 8, className }) {
    /**
     * 手機的展開狀態。桌面不看它（`.cui-notice-toggle` 在 ≥768px 是 `display:none`），
     * 所以同一個元件在兩個尺寸都不需要額外 prop。
     */
    const [open, setOpen] = useState(false);
    if (notices.length === 0)
        return null;
    /** 收合細條要顯示哪一則：優先錯誤（sticky 的那種才是「不看不行」），否則最新一則。 */
    const headline = notices.find((notice) => notice.kind === 'error') ?? notices[notices.length - 1];
    const headlineCount = notices.reduce((sum, notice) => sum + Math.max(1, notice.count), 0);
    return (_jsxs("div", { className: className ? `cui-notice-stack ${className}` : 'cui-notice-stack', "data-testid": "notice-stack", "data-open": open ? 'true' : 'false', "aria-live": "polite", "aria-label": "\u901A\u77E5", style: {
            position: 'fixed',
            top,
            right: 8,
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            width: 'min(360px, calc(100vw - 16px))',
            maxHeight: '60vh',
            overflowY: 'auto',
            pointerEvents: 'auto',
        }, children: [_jsxs("button", { type: "button", className: "cui-notice-toggle", onClick: () => setOpen((value) => !value), "aria-expanded": open, "data-testid": "notice-toggle", "data-state": open ? 'open' : 'closed', children: [_jsxs("span", { className: "cui-notice-toggle-label", children: [NOTICE_KIND_LABEL[headline.kind], headlineCount > 1 ? ` ×${headlineCount}` : ''] }), _jsx("span", { className: "cui-notice-toggle-message", children: headline.message }), _jsx("span", { className: "cui-notice-toggle-chevron", "aria-hidden": "true", children: open ? '⌄' : '⌃' })] }), notices.map((notice) => {
                const style = KIND_STYLE[notice.kind];
                return (_jsxs("div", { className: "cui-notice-item", role: notice.kind === 'error' ? 'alert' : 'status', "data-testid": "notice-item", "data-kind": notice.kind, "data-sticky": notice.sticky ? 'true' : 'false', style: {
                        border: `1px solid ${style.border}`,
                        background: style.background,
                        color: style.foreground,
                        borderRadius: 4,
                        padding: '6px 8px',
                        fontSize: 11,
                        lineHeight: 1.45,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx("span", { style: {
                                        flex: 'none',
                                        color: style.badge,
                                        fontWeight: 600,
                                        fontSize: 10,
                                        border: `1px solid ${style.border}`,
                                        borderRadius: 3,
                                        padding: '0 4px',
                                    }, "data-testid": "notice-kind", children: NOTICE_KIND_LABEL[notice.kind] }), _jsx("span", { style: { flex: 'none', color: 'var(--ak-color-text-tertiary, #6b7280)', fontVariantNumeric: 'tabular-nums' }, children: noticeClock(notice.at) }), notice.count > 1 ? (_jsxs("span", { style: { flex: 'none', color: style.badge }, "data-testid": "notice-count", children: ["\u00D7", notice.count] })) : null, _jsx("span", { style: { flex: 1, minWidth: 0, wordBreak: 'break-word' }, "data-testid": "notice-message", children: notice.message }), notice.kind === 'error' ? (
                                /* 舊鍵盤腳本（scripts/keyboard-round.mjs）讀 `console-error` 的 innerText；
                                   保留一個不可見的鏡像，讓它讀得到錯誤文字。 */
                                _jsx("span", { "data-testid": "console-error", style: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0 }, children: notice.message })) : null, _jsx("button", { type: "button", onClick: () => onDismiss(notice.id), "aria-label": "\u95DC\u9589\u9019\u5247\u901A\u77E5", "data-testid": "notice-dismiss", style: {
                                        flex: 'none',
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'inherit',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        lineHeight: 1,
                                        padding: '0 2px',
                                        textDecoration: 'underline',
                                    }, children: "\u00D7" })] }), notice.detail ? (_jsx("p", { style: { margin: '4px 0 0', color: 'var(--ak-color-text-tertiary, #6b7280)', wordBreak: 'break-word' }, "data-testid": "notice-detail", children: notice.detail })) : null, notice.retry && onRetry ? (_jsx("button", { type: "button", onClick: () => onRetry(notice), "data-testid": "notice-retry", style: {
                                marginTop: 4,
                                border: `1px solid ${style.border}`,
                                background: 'transparent',
                                color: 'inherit',
                                borderRadius: 3,
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: '1px 6px',
                            }, children: "\u91CD\u8A66" })) : null] }, notice.id));
            })] }));
}
//# sourceMappingURL=NoticeStack.js.map